import { MatchingProfileRow } from '@uplift/types';
import { StepFormikContainer } from './StepFormContainer.component';
import { BioForm, ClientAgesForm, CommunityExpertiseForm, EthnicityForm, FaithForm, GenderForm, PronounsForm, SpecialtiesForm, SpokenLanguagesForm, TherapueticStyleForm, TreatmentApproachForm, TypesOfTherapyForm, useProfileFormikConfigs } from '../forms';
import { useQueryClient } from '@tanstack/react-query';
import { PROFILE_QUERY_KEY } from '../profile.utils';
import { useProfileContext } from '../profile.context';
import { ProviderMatchingProfileBookmark } from '@uplift/api';
import { Box, List, ListItem, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Alert, Button } from '@uplift/ui-v2';
import { specialtiesFueValidationSchema } from '../forms/validation';

export type StepFormSwitchProps = {
  step: ProviderMatchingProfileBookmark;
  onNext: () => void;
  onBack: () => void;
};

export const StepFormSwitch: React.VFC<StepFormSwitchProps> = props => {
  const { classes } = useStepFormSwitchStyles();
  const queryClient = useQueryClient();
  const { profile } = useProfileContext();
  const { getFormikConfig } = useProfileFormikConfigs({
    onFormSubmitSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY(profile.user.id) });

      /**
        * Ensures that the new data (after profile invalidation) rerenders
        * preview components before the next step transition animation starts.
        * Without this empty promise, the transition animation starts at the same time
        * the new data is rendered, resulting in no transition animation.
        */
      await new Promise((resolve) => setTimeout(resolve));
      props.onNext();
    },
  });

  switch (props.step) {
    case ProviderMatchingProfileBookmark.supportedAgeGroups:
      return (
        <StepFormikContainer
          title="What age groups do you work with?"
          form={<ClientAgesForm enableButtonStyle />}
          formikProps={getFormikConfig(MatchingProfileRow.supportedAgeGroups)}
          onSkip={props.onNext}
        />
      );
    case ProviderMatchingProfileBookmark.modalities:
      return (
        <StepFormikContainer
          title="What types of therapy do you do?"
          form={<TypesOfTherapyForm enableButtonStyle />}
          formikProps={getFormikConfig(MatchingProfileRow.modalities)}
          onBack={props.onBack}
          onSkip={props.onNext}
        />
      );
    case ProviderMatchingProfileBookmark.specialties:
      return (
        <StepFormikContainer
          title="What areas do you specialize in?"
          description={'Highlight your top specialties but don\'t be afraid to add more. Providers who <b>add 5 or more specialties get more bookings</b>: BetterHelp uses profile specialties to figure out which providers to show clients during matching.<br /><br />Common client searches include <b>Anxiety, Depression, ADD/ADHD, and LGBTQIA+ identity.</b>'}
          form={<SpecialtiesForm hideLabel />}
          formikProps={{
            ...getFormikConfig(MatchingProfileRow.specialties),
            validationSchema: specialtiesFueValidationSchema,
          }}
          onBack={props.onBack}
          onSkip={props.onNext}
          requireValidation
          disableNextWhenInvalid
        />
      );
    case ProviderMatchingProfileBookmark.treatmentApproaches:
      return (
        <StepFormikContainer
          title="What treatment approaches do you have experience using?"
          description="Add any other approaches you use in your treatment. For example, many providers use some sort of <b>Compassion Focused, Emotionally Focused, Mindfulness-Based or Person-Centered therapy</b>."
          form={<TreatmentApproachForm hideLabel />}
          formikProps={getFormikConfig(MatchingProfileRow.treatmentApproach)}
          onBack={props.onBack}
          onSkip={props.onNext}
        />
      );
    case ProviderMatchingProfileBookmark.communityExpertise:
      return (
        <StepFormikContainer
          title="What communities have you worked with?"
          description="Add any identities and communities that you <b>have expertise or experience working in</b>. Clients sometimes seek providers who understand their background or the life experiences of certain identities or communities."
          form={<CommunityExpertiseForm hideLabel />}
          formikProps={getFormikConfig(MatchingProfileRow.communityExpertise)}
          onBack={props.onBack}
          onSkip={props.onNext}
        />
      );
    case ProviderMatchingProfileBookmark.bio:
      return (
        <StepFormikContainer
          title="What would you like to say to potential clients?"
          description={(
            <Box className={classes.bioDescription}>
              <Typography>Let your clinical experience in the rest of your profile speak for itself. Therapy is a personal relationship that asks clients to be vulnerable—so use this space to show your personality and let clients get to know you.</Typography>
              <Typography>Here’s some ideas:</Typography>
              <List className={classes.discList} disablePadding>
                {[
                  'Use plain language that mirrors clients’ emotional state, such as “uncertainty” or feeling “stuck.”',
                  'Include values you practice.',
                  'Write the change or growth you hope clients experience through your therapy.',
                  'Center clients using “you,” e.g. “Meet your full potential” or “Things can get better for you.”',
                ].map((item, index) => (
                  <ListItem key={index} className={classes.discListItem}>{item}</ListItem>
                ))}
              </List>
              <Box my={2}>
                <Alert severity="info" action={<Button className={classes.skipButton} onClick={props.onNext}>Skip</Button>}>
                  Writing a bio on the spot can be hard. You can skip this step for now and come back to it when you've had more time to think.
                </Alert>
              </Box>
            </Box>
          )}
          form={<BioForm />}
          formikProps={getFormikConfig(MatchingProfileRow.bio)}
          onBack={props.onBack}
          onSkip={props.onNext}
        />
      );
    case ProviderMatchingProfileBookmark.languages:
      return (
        <StepFormikContainer
          title="During your sessions, what other languages do you speak besides English?"
          form={<SpokenLanguagesForm hideLabel />}
          formikProps={getFormikConfig(MatchingProfileRow.languages)}
          onBack={props.onBack}
          onSkip={props.onNext}
        />
      );
    case ProviderMatchingProfileBookmark.gender:
      return (
        <StepFormikContainer
          title="What gender(s) do you identify as?"
          description={`
            During matching, some clients feel more comfortable doing therapy with providers of certain genders. Dispaying your gender informs clients about your sense of self, rather than relying on assumptions.
            <br /><br />
            This field asks for your "gender," not your biological "sex." Though the terms are often used interchangeably and are connected, gender and sex aren't the same. To learn more, we recommend Reimagine Gender's <a href="https://www.reimaginegender.org/understanding-gender" target="_blank">resource about Understanding Gender</a>.
            `}
          form={<GenderForm hideLabel includePreferNotToSay />}
          formikProps={getFormikConfig(MatchingProfileRow.gender)}
          onBack={props.onBack}
          onSkip={props.onNext}
          requireValidation
        />
      );
    case ProviderMatchingProfileBookmark.faiths:
      return (
        <StepFormikContainer
          title="What faith(s) do you practice?"
          description="You can use this field to display your faith. Clients sometimes seek providers who understand or share their faith background."
          form={<FaithForm hideLabel includePreferNotToSay />}
          formikProps={getFormikConfig(MatchingProfileRow.faith)}
          onBack={props.onBack}
          onSkip={props.onNext}
        />
      );
    case ProviderMatchingProfileBookmark.ethnicities:
      return (
        <StepFormikContainer
          title="What ethnicity or ethnicities do you identify as?"
          description="You can use this to display your race and ethnicity. Clients sometimes seek providers who understand how their culture, race, heritage, or ethnic background shapes their experience."
          form={<EthnicityForm hideLabel includePreferNotToSay />}
          formikProps={getFormikConfig(MatchingProfileRow.ethnicity)}
          onBack={props.onBack}
          onSkip={props.onNext}
        />
      );
    case ProviderMatchingProfileBookmark.therapeuticStyles:
      return (
        <StepFormikContainer
          title="How would you describe your therapeutic style?"
          description="Give clients a <b>feel</b> for how you structure sessions."
          form={<TherapueticStyleForm hideLabel />}
          formikProps={getFormikConfig(MatchingProfileRow.therapeuticStyle)}
          onBack={props.onBack}
          onSkip={props.onNext}
        />
      );
    case ProviderMatchingProfileBookmark.pronouns:
      return (
        <StepFormikContainer
          title="What are your pronouns?"
          description="Pronouns inform clients about how to refer to you, rather than relying on assumptions. You can choose to display the pronouns you use, based on how comfortable you are with sharing."
          form={<PronounsForm hideLabel />}
          formikProps={getFormikConfig(MatchingProfileRow.pronouns)}
          onBack={props.onBack}
          onSkip={props.onNext}
        />
      );
    default:
      return null;
  }
};

const useStepFormSwitchStyles = makeStyles({ name: 'StepFormSwitch' })(theme => ({
  bioDescription: {
    color: theme.palette.text.secondary,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  discList: {
    listStyleType: 'disc',
    paddingLeft: theme.spacing(3),
  },
  discListItem: {
    display: 'list-item',
    padding: theme.spacing(0.5, 0),
  },
  skipButton: {
    fontWeight: 450,
  }
}));
